import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";
import InternalLinkBlock from "@/components/InternalLinkBlock";
import { servicesBySlugs, SERVICES_FOR_ARTICLE } from "@/data/services";

/**
 * Shared layout for individual Inzichten articles.
 * Keeps a calm editorial column matching SovEU's institutional style.
 */
export default function ArticleLayout({
  testid,
  category,
  title,
  lastUpdated,
  seoTitle,
  seoDescription,
  path,
  shortAnswer,
  children,
  faqs = [],
  sources = [],
  product, // { name, blurb }
  related = [],
}) {
  const canonical = `https://soveu.nl${path}`;
  const slug = path.split("/").filter(Boolean).pop();
  const relevantServices = servicesBySlugs(
    SERVICES_FOR_ARTICLE[slug] || []
  ).map((s) => ({
    to: s.to,
    label: s.navLabel,
    summary: s.summary,
    eyebrow: "Dienst",
  }));
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://soveu.nl/" },
      { "@type": "ListItem", position: 2, name: "Inzichten", item: "https://soveu.nl/inzichten" },
      { "@type": "ListItem", position: 3, name: title, item: canonical },
    ],
  };
  const textFaqs = faqs.filter((f) => typeof f.a === "string");
  const faqSchema =
    textFaqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: textFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    datePublished: "2026-05-08",
    dateModified: "2026-05-08",
    author: { "@type": "Organization", name: "SovEU" },
    publisher: {
      "@type": "Organization",
      name: "SovEU",
      logo: { "@type": "ImageObject", url: "https://soveu.nl/logo.png" },
    },
    mainEntityOfPage: canonical,
    image: "https://soveu.nl/og-image.png",
    description: seoDescription,
  };

  return (
    <div data-testid={testid} className="pt-[96px]">
      <SeoHead title={seoTitle} description={seoDescription} path={path} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      {/* Hero */}
      <section className="section bg-white pb-10">
        <div className="container-soveu max-w-3xl">
          <FadeIn>
            <Link
              to="/inzichten"
              className="text-[13px] font-medium tracking-wide uppercase mb-4 inline-flex items-center gap-1 hover:underline underline-offset-4"
              style={{ color: "#2D6F8F" }}
            >
              ← Inzichten
            </Link>
            <div className="eyebrow mb-3">{category}</div>
            <h1 className="h1-display mb-6">{title}</h1>
            <p className="text-[14px] text-[#6B7280] inline-flex items-center gap-2">
              <Calendar size={14} /> Laatst bijgewerkt: {lastUpdated}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Kort antwoord */}
      {shortAnswer && (
        <section className="bg-white pb-10">
          <div className="container-soveu max-w-3xl">
            <FadeIn>
              <aside
                className="rounded-md p-7 border-l-4"
                style={{
                  backgroundColor: "#F5F7FA",
                  borderColor: "#1E3A5F",
                }}
                data-testid="article-short-answer"
              >
                <h2 className="text-[18px] font-semibold mb-3" style={{ color: "#1E3A5F" }}>
                  Kort antwoord
                </h2>
                <div className="space-y-3 text-[15px] text-[#334155] leading-relaxed">
                  {shortAnswer}
                </div>
              </aside>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="bg-white pb-12">
        <div className="container-soveu max-w-3xl article-body space-y-6 text-[#334155] body-lg leading-relaxed">
          {children}
        </div>
      </section>

      {/* Product CTA */}
      {product && (
        <section className="section" style={{ backgroundColor: "#F5F7FA" }}>
          <div className="container-soveu max-w-3xl">
            <FadeIn>
              <h2 className="h2-display mb-5">Hoe SovEU helpt</h2>
              <p className="body-lg text-[#334155] leading-relaxed mb-7">
                {product.blurb}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/diensten" className="btn-primary" data-testid="article-cta-diensten">
                  Bekijk onze diensten
                  <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="btn-primary btn-ghost" data-testid="article-cta-contact">
                  Plan een gesprek
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="section bg-white">
          <div className="container-soveu max-w-3xl">
            <FadeIn>
              <h2 className="h2-display mb-8">Veelgestelde vragen</h2>
            </FadeIn>
            <div className="space-y-6">
              {faqs.map((q, i) => (
                <FadeIn key={i} delay={i * 60}>
                  <article className="border-t border-[#E5E9EF] pt-6">
                    <h3 className="text-[18px] font-semibold mb-2" style={{ color: "#1E3A5F" }}>
                      {q.q}
                    </h3>
                    <div className="text-[#334155] leading-relaxed text-[15px]">
                      {q.a}
                    </div>
                  </article>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <section className="bg-white pb-16">
          <div className="container-soveu max-w-3xl">
            <FadeIn>
              <h2 className="text-[20px] font-semibold mb-5" style={{ color: "#1E3A5F" }}>
                Bronnen
              </h2>
              <ol className="list-decimal list-outside ml-5 space-y-2 text-[14px] text-[#6B7280]">
                {sources.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Relevante diensten */}
      <InternalLinkBlock
        eyebrow="Relevante diensten"
        title="Relevante diensten"
        intro="Diensten van SovEU die aansluiten op de vragen uit dit artikel."
        items={relevantServices}
        background="#FFFFFF"
        testid="article-relevante-diensten"
      />

      {/* Related */}
      {related.length > 0 && (
        <section className="section" style={{ backgroundColor: "#F5F7FA" }}>
          <div className="container-soveu max-w-3xl">
            <FadeIn>
              <h2 className="h2-display mb-7">Lees ook</h2>
            </FadeIn>
            <div className="space-y-3">
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="block soveu-card p-6 hover:shadow-md transition-shadow"
                  data-testid={`related-${r.to}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="eyebrow mb-2 text-[12px]">{r.category}</div>
                      <h3 className="h3-display text-[18px]">{r.title}</h3>
                    </div>
                    <ArrowRight size={20} className="flex-shrink-0 mt-1" style={{ color: "#2D6F8F" }} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
